import * as PERMISSIONS from "src/constants/permissions";

const PermissionSection = {
    APP: "app",
    CHAT: "chat",
    WEB: "web"
};

const SYNCHRONIZED_CHILD_PERMISSION_IDS = [
    PERMISSIONS.CLIENT_NOTICEBOARD
];

export const HIDDEN_LEGACY_PERMISSION_IDS = [
    PERMISSIONS.WEB_DASHBOARD_ACCESS
];

export const PERMISSION_PRESENTATION_SECTIONS = [
    {
        key: PermissionSection.APP,
        title: "App Permissions",
        items: [
            {
                id: PERMISSIONS.NOTICEBOARD_APP,
                label: "Noticeboard",
                description: "View and post messages, images and video to both Employees and Clients in the Body & Mind feature.",
                children: [
                    {
                        id: PERMISSIONS.CLIENT_NOTICEBOARD,
                        label: "Client Noticeboard",
                        description: "Post messages, images and videos to Clients in the Noticeboard"
                    },
                    {
                        id: PERMISSIONS.SOCIAL_MEDIA_POSTING,
                        label: "Social Media Posting",
                        description: "Allow user to have the ability to post to linked social media accounts via your Me Business app."
                    }
                ]
            },
            {
                id: PERMISSIONS.CLIENT_NOTICEBOARD,
                label: "Client Noticeboard",
                description: "Post messages, images and videos to Clients in the Noticeboard",
                showWhenMissing: PERMISSIONS.NOTICEBOARD_APP
            },
            {
                id: PERMISSIONS.TEAM_GOALS,
                label: "Team Goals",
                description: "Create team goals, assign users, track progress, and keep employees or clients aligned."
            },
            {
                id: PERMISSIONS.CHECKLISTS,
                label: "Checklists",
                description: "Create checklists, assign tasks, set due dates, and track completion."
            },
            {
                id: PERMISSIONS.CREATE_BUSINESS_PLAYLISTS,
                label: "Playlists",
                description: "Can create custom business playlists."
            },
            {
                id: PERMISSIONS.SURVEYS,
                label: "Surveys",
                description: "Create surveys, collect feedback, and view real-time results and insights."
            },
            {
                id: PERMISSIONS.POLLS,
                label: "Polls",
                description: "Create quick polls, gather opinions, and view results instantly."
            }
        ]
    },
    {
        key: PermissionSection.CHAT,
        title: "Chat Permissions (App & Web)",
        items: [
            {
                id: PERMISSIONS.FULL_ACCESS_BUSINESS_CHAT,
                label: "Full Access Business Chat",
                description: "Can message both Employees and Clients as well as create group chats."
            },
            {
                id: PERMISSIONS.MESSAGE_EMPLOYEES,
                label: "Message Employees",
                description: "Can view list of Employees and send direct message in chat."
            },
            {
                id: PERMISSIONS.MESSAGE_CLIENTS,
                label: "Message Clients",
                description: "Can view list of Clients and send direct message in chat."
            },
            {
                id: PERMISSIONS.CREATE_GROUP_CHAT,
                label: "Create Group Chat",
                description: "Create a group chat for Employees, Clients or both."
            },
            {
                id: PERMISSIONS.LEADERSHIP_ROLES,
                label: "Leadership Roles",
                description: "Create and assign leadership roles that appear in chat, making key people visible for support, guidance, or escalation."
            }
        ]
    },
    {
        key: PermissionSection.WEB,
        title: "Web Permissions",
        items: [
            {
                id: PERMISSIONS.MY_ME_APP,
                label: "My Me App",
                description: "Customise the Me Business app. Upload logo and select colours for app interface."
            },
            {
                id: PERMISSIONS.USER_LISTS,
                label: "My User Lists",
                description: "Send user app invites, view user information, manage permissions for all user."
            },
            {
                id: PERMISSIONS.NOTICEBOARD,
                label: "Noticeboard",
                description: "View and post announcements, images and video to both Employees and Clients in the Body & Mind feature.",
                children: [
                    {
                        id: PERMISSIONS.CLIENT_NOTICEBOARD,
                        label: "Client Noticeboard",
                        description: "Post messages, images and videos to Clients in the Noticeboard"
                    },
                    {
                        id: PERMISSIONS.SOCIAL_MEDIA_LINKING_POSTING,
                        label: "Social Media Linking & Posting",
                        description: "Allow user to manage and link new social accounts as well as post to linked social media accounts."
                    }
                ]
            },
            {
                id: "calendar",
                label: "Calendar",
                description: "Allows users to manage Calendar items and item types which affect all users.",
                isPermissionGroup: true,
                children: [
                    {
                        id: PERMISSIONS.CALENDAR_EMPLOYEE_ADMIN,
                        label: "Calendar Employee Admin",
                        description: "Allows users to manage Calendar items visible to Employees."
                    },
                    {
                        id: PERMISSIONS.CALENDAR_CLIENT_ADMIN,
                        label: "Calendar Client Admin",
                        description: "Allows users to manage Calendar items visible to Clients."
                    }
                ]
            },
            {
                id: PERMISSIONS.MY_QUOTE,
                label: "My Quotes",
                description: "Allows users to control which quotes are seen in the mobile app, including creating custom quotes."
            },
            {
                id: PERMISSIONS.SURVEYS,
                label: "Surveys",
                description: "Create surveys, collect feedback, and view real-time results and insights."
            },
            {
                id: PERMISSIONS.POLLS,
                label: "Polls",
                description: "Create quick polls, gather opinions, and view results instantly."
            },
            {
                id: PERMISSIONS.CREATE_BUSINESS_PLAYLISTS,
                label: "Playlists",
                description: "Can create custom business playlists."
            },
            {
                id: PERMISSIONS.BODY_AND_MIND,
                label: "Body and Mind",
                description: "Create and manage Body and Mind categories, subcategories, and content for your business."
            },
            {
                id: PERMISSIONS.STATISTICS,
                label: "Statistics",
                description: "View current app statistics for both Employees and Clients using your business app."
            },
            {
                id: PERMISSIONS.EMAIL_CAMPAIGNS,
                label: "Email Campaigns",
                description: "Allows user to create and send email campaigns, manage audiences, and track campaign performance."
            },
            {
                id: PERMISSIONS.BUSINESS_DIRECTORY,
                label: "Directory",
                description: "Create a list of business contacts that can be visible in the app to either your employees, clients or both."
            },
            {
                id: PERMISSIONS.PERKS,
                label: "Perks",
                description: "Create and manage perks, rewards, and special offers for your users."
            },
            {
                id: PERMISSIONS.DONATIONS,
                label: "Donations",
                description: "Create and manage donation opportunities for employees and clients to support charitable causes."
            },
            {
                id: PERMISSIONS.DIGITAL_MEMBER_CARDS,
                label: "Digital Member Cards",
                description: "Allows users to create and manage employee and client member cards for your business."
            },
            {
                id: PERMISSIONS.ADVERTISING,
                label: "Advertising",
                description: "Allows user to create and manage ad campaigns that will appear in your Me Business app."
            },
            {
                id: PERMISSIONS.SALES_AND_PROMOTIONS,
                label: "Sales & Promotions",
                description: "Purchase & create Client Licenses at a discount rate to issue to clients.",
                children: [
                    {
                        id: PERMISSIONS.DOCUMENTS_VIEW,
                        label: "View Uploaded Documents",
                        description: "Allow user to manage and view other users uploaded documents related to their license."
                    }
                ]
            },
            {
                id: PERMISSIONS.DOCUMENTS_VIEW,
                label: "View Uploaded Documents",
                description: "Allow user to manage and view other users uploaded documents related to their license.",
                showWhenMissing: PERMISSIONS.SALES_AND_PROMOTIONS
            },
            {
                id: PERMISSIONS.BILLING,
                label: "Billing & Subscriptions",
                description: "Manage your payments, payment methods and subscriptions."
            }
        ]
    }
];

export const buildPermissionLookup = (permissions) => {
    return Object.values(permissions || {}).flat().reduce((lookup, permission) => ({
        ...lookup,
        [permission.id]: permission
    }), {});
};

const hasPermission = (permissionLookup, id) => Boolean(permissionLookup[id]);

const isPermissionGroup = (item) => Boolean(item.isPermissionGroup);

const isPermissionGroupId = (id) => PERMISSION_PRESENTATION_SECTIONS
    .flatMap(section => section.items)
    .some(item => item.id === id && isPermissionGroup(item));

const isSynchronizedChildPermission = (item) => (
    item.parentId && SYNCHRONIZED_CHILD_PERMISSION_IDS.includes(item.id)
);

const getSelectionId = (id, parentId = null) => (
    parentId && getParentPermissionIds(id).length > 1 && !SYNCHRONIZED_CHILD_PERMISSION_IDS.includes(id) ? `${parentId}:${id}` : id
);

const getDisplayLabel = (item, customPermissionLabels) => customPermissionLabels[item.id] || item.label;

const toDisplayItem = (item, permissionLookup, customPermissionLabels = {}, parentId = null, depth = 0) => ({
    ...permissionLookup[item.id],
    id: item.id,
    name: getDisplayLabel(item, customPermissionLabels),
    description: item.description,
    isPermissionGroup: isPermissionGroup(item),
    parentId,
    depth,
    selectionId: getSelectionId(item.id, parentId)
});

export const getDisplaySections = (permissions, sectionKeys = [PermissionSection.APP, PermissionSection.CHAT, PermissionSection.WEB], customPermissionLabels = {}, hiddenPermissionIds = []) => {
    const permissionLookup = buildPermissionLookup(permissions);
    const hiddenPermissionIdSet = new Set([...HIDDEN_LEGACY_PERMISSION_IDS, ...hiddenPermissionIds]);

    return PERMISSION_PRESENTATION_SECTIONS
        .filter(section => sectionKeys.includes(section.key))
        .map(section => ({
            ...section,
            items: section.items.reduce((items, item) => {
                if (hiddenPermissionIdSet.has(item.id)) {
                    return items;
                }

                if (item.showWhenMissing && hasPermission(permissionLookup, item.showWhenMissing)) {
                    return items;
                }

                if (!isPermissionGroup(item) && !hasPermission(permissionLookup, item.id)) {
                    return items;
                }

                if (isPermissionGroup(item) && !(item.children || []).some(child => hasPermission(permissionLookup, child.id))) {
                    return items;
                }

                const parent = toDisplayItem(item, permissionLookup, customPermissionLabels);
                const children = (item.children || [])
                    .filter(child => hasPermission(permissionLookup, child.id))
                    .filter(child => !hiddenPermissionIdSet.has(child.id))
                    .map(child => toDisplayItem(child, permissionLookup, customPermissionLabels, item.id, 1));

                return [...items, parent, ...children];
            }, [])
        }))
        .filter(section => section.items.length > 0);
};

export const getChildPermissionIds = (parentId) => {
    return PERMISSION_PRESENTATION_SECTIONS.flatMap(section => section.items)
        .filter(item => item.id === parentId)
        .flatMap(item => item.children || [])
        .map(child => child.id);
};

export const getParentPermissionIds = (childId) => {
    return PERMISSION_PRESENTATION_SECTIONS.flatMap(section => section.items)
        .filter(item => (item.children || []).some(child => child.id === childId))
        .map(item => item.id);
};

export const isPermissionDisplayChecked = (item, selectedPermissionIds) => {
    if (item.isPermissionGroup) {
        return getChildPermissionIds(item.id).some(childId => selectedPermissionIds.includes(childId));
    }

    if (isSynchronizedChildPermission(item)) {
        return selectedPermissionIds.includes(item.selectionId);
    }

    if (item.parentId && !isPermissionGroupId(item.parentId) && !selectedPermissionIds.includes(item.parentId)) {
        return false;
    }

    return selectedPermissionIds.includes(item.selectionId);
};

export const isPermissionDisplayDisabled = (item, selectedPermissionIds, disableInput) => (
    disableInput || Boolean(
        item.parentId
        && !isPermissionGroupId(item.parentId)
        && !isSynchronizedChildPermission(item)
        && !selectedPermissionIds.includes(item.parentId)
    )
);

const parseSelectionId = (selectionId) => {
    if (typeof selectionId !== "string") {
        return {
            permissionId: selectionId,
            parentId: null
        };
    }

    if (isPermissionGroupId(selectionId)) {
        return {
            permissionId: selectionId,
            parentId: null
        };
    }

    const [parentId, permissionId] = selectionId.split(":").map(Number);
    return { parentId, permissionId };
};

export const getDisplayPermissionSelection = (permissionIds) => {
    const selectedPermissionIds = Array.isArray(permissionIds) ? permissionIds.map(Number) : [];
    const duplicateChildIds = PERMISSION_PRESENTATION_SECTIONS.flatMap(section => section.items)
        .flatMap(item => item.children || [])
        .map(child => child.id)
        .filter(id => !SYNCHRONIZED_CHILD_PERMISSION_IDS.includes(id))
        .filter((id, index, ids) => ids.indexOf(id) !== index);
    const duplicateChildIdSet = new Set(duplicateChildIds);

    return selectedPermissionIds.reduce((selection, permissionId) => {
        if (!duplicateChildIdSet.has(permissionId)) {
            return [...selection, permissionId];
        }

        const selectedParentKeys = getParentPermissionIds(permissionId)
            .filter(parentId => selectedPermissionIds.includes(parentId))
            .map(parentId => getSelectionId(permissionId, parentId));

        return [...selection, ...selectedParentKeys];
    }, []);
};

export const getSavePermissionSelection = (selectedPermissionIds) => {
    const normalized = selectedPermissionIds
        .filter(selectionId => !isPermissionGroupId(selectionId))
        .map(selectionId => parseSelectionId(selectionId).permissionId);
    return [...new Set(normalized)].sort((a, b) => a - b);
};

export const getNextPermissionSelection = (selectedPermissionIds, selectionId, isChecked) => {
    const { permissionId } = parseSelectionId(selectionId);

    if (isChecked) {
        if (isPermissionGroupId(permissionId)) {
            return [...new Set([...selectedPermissionIds, ...getChildPermissionIds(permissionId)])].sort();
        }
        return [...new Set([...selectedPermissionIds, selectionId])].sort();
    }

    const childIds = getChildPermissionIds(permissionId);
    const parentsAfterChange = selectedPermissionIds.filter(id => id !== selectionId);

    return selectedPermissionIds
        .filter(id => id !== selectionId)
        .filter(id => {
            const { permissionId: selectedPermissionId, parentId } = parseSelectionId(id);
            if (!childIds.includes(selectedPermissionId)) {
                return true;
            }

            if (parentId) {
                return parentId !== permissionId;
            }

            const alternateParents = getParentPermissionIds(selectedPermissionId).filter(parentPermissionId => parentPermissionId !== permissionId);
            return alternateParents.some(parentPermissionId => parentsAfterChange.includes(parentPermissionId));
        })
        .sort();
};
